// @ts-nocheck
/**
 * 質屋画面
 * 差し押さえられたカードを基礎価格で購入できる
 *
 * サーバー連携対応:
 * - マーケットプレイス: 他プレイヤーの差し押さえカード
 * - 買い戻し: 自分の差し押さえカード（10%割引）
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  GORUDO,
  getGorudoQuote,
  getPawnShopSummary,
  filterInventory,
  sortInventory,
  purchaseFromPawnShop,
  purchaseAllFromPawnShop,
  calculateDiscount,
} from '../systems/pawnShopManager';
import { collectionManager } from '../../systems/collectionManager';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { ATTRIBUTE_COLORS } from '../../../utils/constants';
import {
  EFFECT_LEVELS,
  RARITY_KEYFRAMES,
  applyRarityStyle,
  getRarityOverlay,
  getParticleCount,
  getParticleStyle,
  hasDoubleBorder,
  hasCornerOrnaments,
} from '../../../styles/rarityEffects';
import { RARITY_COLORS } from '../../data/constants';
import {
  fetchMarketplace,
  fetchOwnInventory,
  purchaseItem,
  buybackItem,
  isPawnShopServerAvailable,
  PawnShopItem,
} from '../../../lib/usePawnShopServer';

// タイプアイコン
const TYPE_ICONS = {
  monster: '⚔️',
  magic: '✨',
  field: '🏔️',
  phasecard: '🔮',
};

// 買い戻し割引率
const BUYBACK_DISCOUNT_RATE = 0.1; // 10%

const PawnShop = ({
  playerData,
  updatePlayerData,
  allCards = [],
  getMarketPrice, // オプション: 市場価格取得関数
  onClose,
  onRefreshPlayerData, // サーバーからプレイヤーデータを再取得するコールバック
}) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'marketplace' | 'buyback' | 'local'>('marketplace');
  const [sortBy, setSortBy] = useState('rarity');
  const [ascending, setAscending] = useState(true);
  const [filterRarity, setFilterRarity] = useState('');
  const [filterAttribute, setFilterAttribute] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [gorudoQuote, setGorudoQuote] = useState(() => getGorudoQuote('greeting'));

  // サーバー連携状態
  const [serverAvailable] = useState(() => isPawnShopServerAvailable());
  const [marketplaceItems, setMarketplaceItems] = useState<PawnShopItem[]>([]);
  const [buybackItems, setBuybackItems] = useState<PawnShopItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const pawnShopData = playerData.investmentData?.pawnShop || { inventory: [] };
  const gold = playerData.gold;

  // キーフレームをDOMに注入
  useEffect(() => {
    const styleId = 'rarity-effect-keyframes';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = RARITY_KEYFRAMES;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // サーバーからデータを取得
  const loadServerData = useCallback(async () => {
    if (!serverAvailable) return;

    setLoading(true);
    setError(null);

    try {
      // マーケットプレイスと自分の在庫を並列取得
      const [marketRes, inventoryRes] = await Promise.all([
        fetchMarketplace(
          filterRarity ? { rarity: filterRarity } : undefined,
          sortBy === 'price' ? (ascending ? 'price_asc' : 'price_desc') :
          sortBy === 'rarity' ? 'rarity' : 'listed_desc'
        ),
        fetchOwnInventory(),
      ]);

      if (marketRes.success && marketRes.items) {
        // 自分のカードを除外（buybackで表示するため）
        const otherItems = marketRes.items.filter(item => !item.is_mine);
        setMarketplaceItems(otherItems);
      } else if (marketRes.error) {
        console.warn('[PawnShop] Marketplace error:', marketRes.error);
      }

      if (inventoryRes.success && inventoryRes.items) {
        // available状態の自分のカードのみ（買い戻し可能なもの）
        const myItems = inventoryRes.items.filter(item => item.status === 'available');
        setBuybackItems(myItems);
      } else if (inventoryRes.error) {
        console.warn('[PawnShop] Inventory error:', inventoryRes.error);
      }
    } catch (err) {
      console.error('[PawnShop] Failed to load server data:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [serverAvailable, filterRarity, sortBy, ascending]);

  // 初回ロードとフィルタ変更時にデータ取得
  useEffect(() => {
    loadServerData();
  }, [loadServerData]);

  // カードIDからカード情報を取得するマップを作成
  const cardMap = useMemo(() => {
    const map = {};
    allCards.forEach(card => {
      map[card.id] = card;
    });
    return map;
  }, [allCards]);

  // サマリー
  const summary = useMemo(() => getPawnShopSummary(pawnShopData), [pawnShopData]);

  // フィルタリングとソート（カード情報をマージ）
  const displayedItems = useMemo(() => {
    const filters = {};
    if (filterRarity) filters.rarity = filterRarity;
    if (filterAttribute) filters.attribute = filterAttribute;

    const filtered = filterInventory(pawnShopData, filters);
    const sorted = sortInventory(filtered, sortBy, ascending);

    // カード情報をマージ
    return sorted.map(item => {
      const cardInfo = cardMap[item.cardId] || {};
      return {
        ...item,
        name: cardInfo.name || item.name || item.cardId,
        attribute: cardInfo.attribute || item.attribute || 'なし',
        type: cardInfo.type || 'monster',
        cost: cardInfo.cost ?? 0,
        attack: cardInfo.attack,
        hp: cardInfo.hp,
      };
    });
  }, [pawnShopData, filterRarity, filterAttribute, sortBy, ascending, cardMap]);

  // サーバー経由での購入処理
  const handleServerPurchase = async (isBuyback: boolean = false) => {
    if (!selectedItem || purchasing) return;

    setPurchasing(true);
    try {
      const result = isBuyback
        ? await buybackItem(selectedItem.id)
        : await purchaseItem(selectedItem.id);

      if (!result.success) {
        alert(result.error || '購入に失敗しました');
        return;
      }

      // 成功時: カードをコレクションに追加、ゴールドを減算
      if (result.purchased && updatePlayerData) {
        updatePlayerData((prev) => {
          const updatedData = collectionManager.addCard(
            prev,
            result.purchased.card_id,
            result.purchased.rarity
          );
          return {
            ...updatedData,
            gold: result.new_gold_balance ?? (prev.gold - result.purchased.price),
          };
        });
      }

      // サーバーデータ再取得
      if (onRefreshPlayerData) {
        onRefreshPlayerData();
      }

      setGorudoQuote(
        isBuyback
          ? 'へっへ、買い戻しかい？元の持ち主は優遇するよ'
          : getGorudoQuote('purchase')
      );
      setSelectedItem(null);

      // マーケットプレイスを再読み込み
      await loadServerData();
    } catch (err) {
      console.error('[PawnShop] Purchase failed:', err);
      alert('購入処理中にエラーが発生しました');
    } finally {
      setPurchasing(false);
    }
  };

  // ローカル購入処理（サーバー未接続時のフォールバック）
  const handleLocalPurchase = () => {
    if (!selectedItem) return;

    const result = purchaseFromPawnShop(pawnShopData, selectedItem.pawnId, gold);

    if (!result.success) {
      alert(result.error);
      return;
    }

    // プレイヤーデータ更新
    updatePlayerData((prev) => {
      // collectionManager.addCardで正しくコレクションに追加（既存カードは枚数増加）
      const updatedData = collectionManager.addCard(
        prev,
        result.item.cardId,
        result.item.rarity
      );

      return {
        ...updatedData,
        gold: prev.gold - result.cost,
        investmentData: {
          ...prev.investmentData,
          pawnShop: result.newPawnShopData,
        },
      };
    });

    setGorudoQuote(getGorudoQuote('purchase'));
    setSelectedItem(null);
  };

  // 購入処理（タブに応じて分岐）
  const handlePurchase = () => {
    if (activeTab === 'local') {
      handleLocalPurchase();
    } else {
      handleServerPurchase(activeTab === 'buyback');
    }
  };

  // 全部買い戻し処理
  const handlePurchaseAll = () => {
    const result = purchaseAllFromPawnShop(pawnShopData, gold);

    if (!result.success) {
      alert(result.error);
      return;
    }

    // プレイヤーデータ更新
    updatePlayerData((prev) => {
      // collectionManager.addCardsで正しくコレクションに追加（既存カードは枚数増加）
      const cardsToAdd = result.items.map(item => ({
        cardId: item.cardId,
        rarity: item.rarity,
        quantity: 1,
      }));
      const updatedData = collectionManager.addCards(prev, cardsToAdd);

      return {
        ...updatedData,
        gold: prev.gold - result.totalCost,
        investmentData: {
          ...prev.investmentData,
          pawnShop: result.newPawnShopData,
        },
      };
    });

    setGorudoQuote('へっへっへ、全部持っていくのかい？太っ腹だねぇ！');
    setSelectedItem(null);
  };

  // 全部買い戻し可能かチェック
  const canPurchaseAll = gold >= summary.totalValue && summary.totalItems > 0;

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#2a1810',
      color: '#e0d0c0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      borderBottom: '1px solid #4a3020',
      backgroundColor: '#1a0a00',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 'bold',
      color: '#ffd700',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    closeButton: {
      padding: '8px 16px',
      backgroundColor: '#4a3020',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    npcSection: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      backgroundColor: 'rgba(255, 215, 0, 0.05)',
      borderBottom: '1px solid #4a3020',
    },
    npcIcon: {
      fontSize: '40px',
    },
    npcContent: {
      flex: 1,
    },
    npcName: {
      fontWeight: 'bold',
      color: '#ffd700',
      marginBottom: '4px',
    },
    npcQuote: {
      fontSize: '14px',
      color: '#c0a080',
      fontStyle: 'italic',
    },
    goldInfo: {
      padding: '12px 16px',
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      borderBottom: '1px solid #4a3020',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    goldLabel: {
      color: '#ffd700',
    },
    goldValue: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#ffd700',
    },
    tabBar: {
      display: 'flex',
      borderBottom: '1px solid #4a3020',
      backgroundColor: '#1a0a00',
    },
    tab: {
      flex: 1,
      padding: '12px 16px',
      textAlign: 'center',
      cursor: 'pointer',
      color: '#888',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s ease',
    },
    tabActive: {
      color: '#ffd700',
      borderBottom: '2px solid #ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
    },
    tabBadge: {
      marginLeft: '8px',
      padding: '2px 6px',
      borderRadius: '10px',
      fontSize: '11px',
      backgroundColor: 'rgba(255, 215, 0, 0.3)',
      color: '#ffd700',
    },
    buybackBadge: {
      marginLeft: '8px',
      padding: '2px 6px',
      borderRadius: '10px',
      fontSize: '11px',
      backgroundColor: 'rgba(76, 175, 80, 0.3)',
      color: '#4caf50',
    },
    loadingOverlay: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      color: '#888',
    },
    errorMessage: {
      padding: '16px',
      backgroundColor: 'rgba(255, 0, 0, 0.1)',
      color: '#ff6666',
      textAlign: 'center',
      borderRadius: '8px',
      margin: '16px',
    },
    filterBar: {
      padding: '12px 16px',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderBottom: '1px solid #4a3020',
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    select: {
      padding: '6px 10px',
      backgroundColor: '#1a0a00',
      color: '#e0d0c0',
      border: '1px solid #4a3020',
      borderRadius: '4px',
      fontSize: '14px',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    emptyMessage: {
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? 'repeat(auto-fill, minmax(100px, 1fr))'
        : 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px',
    },
    // カード表示用スタイル
    cardWrapper: {
      cursor: 'pointer',
      transition: 'transform 0.2s ease',
    },
    card: {
      width: '100%',
      aspectRatio: '3/4',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      transition: 'all 0.3s ease',
    },
    cardHeader: {
      padding: '4px 6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'rgba(0,0,0,0.3)',
    },
    costBadge: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: 'bold',
      color: '#fff',
    },
    rarityBadge: {
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 'bold',
      color: '#fff',
    },
    cardBody: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '8px 4px',
    },
    cardName: {
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      lineHeight: '1.3',
    },
    cardStats: {
      fontSize: '10px',
      color: 'rgba(255,255,255,0.8)',
      textAlign: 'center',
      marginTop: '4px',
    },
    cardFooter: {
      padding: '4px 6px',
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    typeIcon: {
      fontSize: '12px',
    },
    priceRow: {
      padding: '3px 6px',
      background: 'rgba(0,0,0,0.3)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    priceBadge: {
      fontSize: '10px',
      fontWeight: 'bold',
      color: '#ffd700',
    },
    discountBadge: {
      fontSize: '9px',
      color: '#4caf50',
      marginLeft: '4px',
    },
    purchaseSection: {
      padding: '16px',
      borderTop: '1px solid #4a3020',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    selectedInfo: {
      marginBottom: '12px',
    },
    purchaseButton: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#ffd700',
      color: '#1a0a00',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    purchaseButtonDisabled: {
      backgroundColor: '#4a3020',
      color: '#888',
      cursor: 'not-allowed',
    },
    summaryBox: {
      padding: '12px',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '14px',
      marginBottom: '4px',
    },
  };

  // 属性の色を取得
  const getAttributeColors = (attribute) => {
    return ATTRIBUTE_COLORS[attribute] || ATTRIBUTE_COLORS['なし'] || {
      bg: 'linear-gradient(135deg, #333 0%, #555 100%)',
      glow: '#888',
    };
  };

  // 現在のタブの表示アイテムを取得
  const getCurrentTabItems = () => {
    if (activeTab === 'marketplace') {
      return marketplaceItems.map(item => ({
        ...item,
        pawnId: item.id,
        cardId: item.card_id,
        name: cardMap[item.card_id]?.name || item.card_name || item.card_id,
        attribute: cardMap[item.card_id]?.attribute || item.attribute || 'なし',
        type: cardMap[item.card_id]?.type || item.card_type || 'monster',
        cost: cardMap[item.card_id]?.cost ?? 0,
        attack: cardMap[item.card_id]?.attack,
        hp: cardMap[item.card_id]?.hp,
        rarity: item.rarity,
        pawnPrice: item.sale_price,
        isBuyback: false,
      }));
    } else if (activeTab === 'buyback') {
      return buybackItems.map(item => ({
        ...item,
        pawnId: item.id,
        cardId: item.card_id,
        name: cardMap[item.card_id]?.name || item.card_name || item.card_id,
        attribute: cardMap[item.card_id]?.attribute || item.attribute || 'なし',
        type: cardMap[item.card_id]?.type || item.card_type || 'monster',
        cost: cardMap[item.card_id]?.cost ?? 0,
        attack: cardMap[item.card_id]?.attack,
        hp: cardMap[item.card_id]?.hp,
        rarity: item.rarity,
        pawnPrice: Math.floor(item.sale_price * (1 - BUYBACK_DISCOUNT_RATE)), // 10%割引
        originalPrice: item.sale_price,
        isBuyback: true,
      }));
    } else {
      // ローカル（フォールバック）
      return displayedItems;
    }
  };

  const currentTabItems = getCurrentTabItems();
  const hasAnyItems = marketplaceItems.length > 0 || buybackItems.length > 0 || summary.hasItems;

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.title}>
          <span>💰</span>
          <span>質屋</span>
        </div>
        <button style={styles.closeButton} onClick={onClose}>
          閉じる
        </button>
      </div>

      {/* ゴルドNPC */}
      <div style={styles.npcSection}>
        <div style={styles.npcIcon}>{GORUDO.icon}</div>
        <div style={styles.npcContent}>
          <div style={styles.npcName}>{GORUDO.name}</div>
          <div style={styles.npcQuote}>「{gorudoQuote}」</div>
        </div>
      </div>

      {/* 所持金 */}
      <div style={styles.goldInfo}>
        <span style={styles.goldLabel}>💰 所持金</span>
        <span style={styles.goldValue}>{gold.toLocaleString()}G</span>
      </div>

      {/* タブバー */}
      {serverAvailable && (
        <div style={styles.tabBar}>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === 'marketplace' ? styles.tabActive : {}),
            }}
            onClick={() => { setActiveTab('marketplace'); setSelectedItem(null); }}
          >
            🏪 マーケット
            {marketplaceItems.length > 0 && (
              <span style={styles.tabBadge}>{marketplaceItems.length}</span>
            )}
          </div>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === 'buyback' ? styles.tabActive : {}),
            }}
            onClick={() => { setActiveTab('buyback'); setSelectedItem(null); }}
          >
            🔄 買い戻し
            {buybackItems.length > 0 && (
              <span style={styles.buybackBadge}>{buybackItems.length}</span>
            )}
          </div>
          {summary.hasItems && (
            <div
              style={{
                ...styles.tab,
                ...(activeTab === 'local' ? styles.tabActive : {}),
              }}
              onClick={() => { setActiveTab('local'); setSelectedItem(null); }}
            >
              📦 ローカル
              <span style={styles.tabBadge}>{summary.totalItems}</span>
            </div>
          )}
        </div>
      )}

      {/* ローディング表示 */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <span>読み込み中...</span>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div style={styles.errorMessage}>
          {error}
          <button
            style={{ marginLeft: '12px', padding: '4px 8px', cursor: 'pointer' }}
            onClick={loadServerData}
          >
            再試行
          </button>
        </div>
      )}

      {/* フィルターバー */}
      <div style={styles.filterBar}>
        <select
          style={styles.select}
          value={filterRarity}
          onChange={(e) => setFilterRarity(e.target.value)}
        >
          <option value="">全レアリティ</option>
          <option value="GR">GR</option>
          <option value="UR">UR</option>
          <option value="SR">SR</option>
          <option value="R">R</option>
          <option value="UC">UC</option>
          <option value="C">C</option>
        </select>

        <select
          style={styles.select}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="rarity">レアリティ順</option>
          <option value="price">価格順</option>
          <option value="name">名前順</option>
        </select>

        <button
          style={{
            ...styles.select,
            cursor: 'pointer',
          }}
          onClick={() => setAscending(!ascending)}
        >
          {ascending ? '↑昇順' : '↓降順'}
        </button>

        <span style={{ marginLeft: 'auto', fontSize: '14px', color: '#888' }}>
          {currentTabItems.length}件
        </span>
      </div>

      {/* サマリー（ローカルタブのみ表示） */}
      {activeTab === 'local' && (
        <div style={{ padding: '12px 16px' }}>
          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}>
              <span>在庫数</span>
              <span>{summary.totalItems}枚</span>
            </div>
            <div style={styles.summaryRow}>
              <span>総価値</span>
              <span>{summary.totalValue.toLocaleString()}G</span>
            </div>
          </div>

          {/* 全部買い戻しボタン */}
          {summary.totalItems > 1 && (
            <button
              style={{
                width: '100%',
                padding: '12px',
                marginTop: '8px',
                backgroundColor: canPurchaseAll ? '#ffd700' : '#4a3020',
                color: canPurchaseAll ? '#1a0a00' : '#888',
                border: 'none',
                borderRadius: '4px',
                cursor: canPurchaseAll ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              onClick={handlePurchaseAll}
              disabled={!canPurchaseAll}
            >
              {canPurchaseAll
                ? `💰 全部買い戻し（${summary.totalValue.toLocaleString()}G）`
                : gold < summary.totalValue
                  ? `所持金不足（必要: ${summary.totalValue.toLocaleString()}G）`
                  : '在庫なし'
              }
            </button>
          )}
        </div>
      )}

      {/* 買い戻しタブの説明 */}
      {activeTab === 'buyback' && buybackItems.length > 0 && (
        <div style={{ padding: '12px 16px', backgroundColor: 'rgba(76, 175, 80, 0.1)', borderBottom: '1px solid #4a3020' }}>
          <div style={{ fontSize: '14px', color: '#4caf50' }}>
            💡 元の持ち主は<strong>10%割引</strong>で買い戻せます
          </div>
        </div>
      )}

      {/* 空の状態 */}
      {!loading && currentTabItems.length === 0 && (
        <div style={styles.content}>
          <div style={styles.emptyMessage}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {activeTab === 'marketplace' ? '🏪' : activeTab === 'buyback' ? '✨' : '📦'}
            </div>
            <div>
              {activeTab === 'marketplace' && 'マーケットプレイスに商品がありません'}
              {activeTab === 'buyback' && '買い戻し可能なカードはありません'}
              {activeTab === 'local' && '在庫がありません'}
            </div>
            <div style={{ fontSize: '12px', marginTop: '8px', color: '#888' }}>
              {activeTab === 'marketplace' && '誰かが借金を返せなくなったら商品が並びます'}
              {activeTab === 'buyback' && '借金を返せず差し押さえられたカードがここに表示されます'}
              {activeTab === 'local' && '誰かが借金を返せなくなったら...へっへ'}
            </div>
          </div>
        </div>
      )}

      {/* 商品一覧 */}
      {!loading && currentTabItems.length > 0 && (
      <div style={styles.content}>
        <div style={styles.grid}>
          {currentTabItems.map((item) => {
            const isSelected = selectedItem?.pawnId === item.pawnId;
            const canAfford = gold >= item.pawnPrice;
            const marketPrice = getMarketPrice ? getMarketPrice(item.cardId, item.rarity) : item.pawnPrice;
            const discount = calculateDiscount(item, marketPrice);
            const colors = getAttributeColors(item.attribute);
            const rarityColor = RARITY_COLORS[item.rarity] || '#888';
            const isMonster = item.type === 'monster';

            // パーティクル数
            const particleCount = getParticleCount(item.rarity, EFFECT_LEVELS.FULL);

            // ベーススタイル
            const baseCardStyle = {
              ...styles.card,
              background: colors.bg,
            };

            // レアリティエフェクト適用
            const cardStyle = applyRarityStyle(baseCardStyle, item.rarity, EFFECT_LEVELS.FULL);

            // オーバーレイスタイル
            const overlayStyle = getRarityOverlay(item.rarity, EFFECT_LEVELS.FULL);

            // 二重枠チェック
            const showDoubleBorder = hasDoubleBorder(item.rarity, EFFECT_LEVELS.FULL);
            const showCornerOrnaments = hasCornerOrnaments(item.rarity, EFFECT_LEVELS.FULL);

            return (
              <div
                key={item.pawnId}
                style={{
                  ...styles.cardWrapper,
                  opacity: canAfford ? 1 : 0.5,
                  transform: isSelected ? 'translateY(-4px) scale(1.02)' : 'none',
                }}
                onClick={() => canAfford && setSelectedItem(item)}
              >
                <div style={{
                  ...cardStyle,
                  boxShadow: isSelected
                    ? `0 0 20px #ffd700, ${cardStyle.boxShadow || ''}`
                    : cardStyle.boxShadow,
                }}>
                  {/* 二重枠（UR, GR用） */}
                  {showDoubleBorder && (
                    <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: '3px',
                      right: '3px',
                      bottom: '3px',
                      border: `1px solid ${rarityColor}80`,
                      borderRadius: '6px',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }} />
                  )}

                  {/* コーナー装飾（GR用） */}
                  {showCornerOrnaments && (
                    <>
                      <div style={{
                        position: 'absolute', top: '-2px', left: '-2px',
                        width: '14px', height: '14px',
                        borderTop: `3px solid ${rarityColor}`,
                        borderLeft: `3px solid ${rarityColor}`,
                        borderRadius: '4px 0 0 0',
                        zIndex: 5,
                      }} />
                      <div style={{
                        position: 'absolute', top: '-2px', right: '-2px',
                        width: '14px', height: '14px',
                        borderTop: `3px solid ${rarityColor}`,
                        borderRight: `3px solid ${rarityColor}`,
                        borderRadius: '0 4px 0 0',
                        zIndex: 5,
                      }} />
                      <div style={{
                        position: 'absolute', bottom: '-2px', left: '-2px',
                        width: '14px', height: '14px',
                        borderBottom: `3px solid ${rarityColor}`,
                        borderLeft: `3px solid ${rarityColor}`,
                        borderRadius: '0 0 0 4px',
                        zIndex: 5,
                      }} />
                      <div style={{
                        position: 'absolute', bottom: '-2px', right: '-2px',
                        width: '14px', height: '14px',
                        borderBottom: `3px solid ${rarityColor}`,
                        borderRight: `3px solid ${rarityColor}`,
                        borderRadius: '0 0 4px 0',
                        zIndex: 5,
                      }} />
                    </>
                  )}

                  {/* レアリティオーバーレイ（光沢、ホロ等） */}
                  {overlayStyle && <div style={overlayStyle} />}

                  {/* パーティクル */}
                  {Array.from({ length: particleCount }).map((_, i) => (
                    <div key={i} style={getParticleStyle(i, item.rarity)} />
                  ))}

                  {/* ヘッダー: コスト & レアリティ */}
                  <div style={{ ...styles.cardHeader, position: 'relative', zIndex: 4 }}>
                    <div style={styles.costBadge}>{item.cost}</div>
                    <div style={{
                      ...styles.rarityBadge,
                      backgroundColor: rarityColor,
                    }}>
                      {item.rarity}
                    </div>
                  </div>

                  {/* ボディ: 名前 & ステータス */}
                  <div style={{ ...styles.cardBody, position: 'relative', zIndex: 4 }}>
                    <div style={styles.cardName}>{item.name}</div>
                    {isMonster && item.attack !== undefined && (
                      <div style={styles.cardStats}>
                        ATK {item.attack} / HP {item.hp}
                      </div>
                    )}
                  </div>

                  {/* フッター: タイプアイコン & 属性 */}
                  <div style={{ ...styles.cardFooter, position: 'relative', zIndex: 4 }}>
                    <span style={styles.typeIcon}>
                      {TYPE_ICONS[item.type] || '📄'}
                    </span>
                    <span style={{ fontSize: '10px', color: colors.glow }}>
                      {item.attribute}
                    </span>
                  </div>

                  {/* 価格行 */}
                  <div style={{ ...styles.priceRow, position: 'relative', zIndex: 4 }}>
                    <span style={styles.priceBadge}>
                      {item.pawnPrice.toLocaleString()}G
                    </span>
                    {/* 買い戻し割引表示 */}
                    {item.isBuyback && item.originalPrice && (
                      <span style={{ ...styles.discountBadge, backgroundColor: 'rgba(76, 175, 80, 0.3)', color: '#4caf50' }}>
                        -10%
                      </span>
                    )}
                    {/* 市場価格との比較 */}
                    {!item.isBuyback && discount.isGoodDeal && (
                      <span style={styles.discountBadge}>
                        -{discount.discountPercent}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* 購入セクション */}
      {selectedItem && (
        <div style={styles.purchaseSection}>
          <div style={styles.selectedInfo}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {selectedItem.name}
            </div>
            <div style={{ fontSize: '14px', color: '#888' }}>
              {selectedItem.rarity} / {selectedItem.attribute} / {TYPE_ICONS[selectedItem.type] || '📄'} {selectedItem.type}
              {selectedItem.type === 'monster' && selectedItem.attack !== undefined && (
                <span> / ATK {selectedItem.attack} / HP {selectedItem.hp}</span>
              )}
            </div>
            {/* 買い戻し割引表示 */}
            {selectedItem.isBuyback && selectedItem.originalPrice && (
              <div style={{ fontSize: '12px', color: '#4caf50', marginTop: '4px' }}>
                <span style={{ textDecoration: 'line-through', color: '#888' }}>
                  {selectedItem.originalPrice.toLocaleString()}G
                </span>
                {' → '}
                <span style={{ fontWeight: 'bold' }}>
                  {selectedItem.pawnPrice.toLocaleString()}G（10%OFF）
                </span>
              </div>
            )}
          </div>
          <button
            style={{
              ...styles.purchaseButton,
              ...(gold < selectedItem.pawnPrice || purchasing ? styles.purchaseButtonDisabled : {}),
              ...(selectedItem.isBuyback && gold >= selectedItem.pawnPrice && !purchasing ? { backgroundColor: '#4caf50' } : {}),
            }}
            onClick={handlePurchase}
            disabled={gold < selectedItem.pawnPrice || purchasing}
          >
            {purchasing
              ? '処理中...'
              : gold >= selectedItem.pawnPrice
                ? selectedItem.isBuyback
                  ? `🔄 買い戻し（${selectedItem.pawnPrice.toLocaleString()}G）`
                  : `💰 購入（${selectedItem.pawnPrice.toLocaleString()}G）`
                : '所持金不足'
            }
          </button>
        </div>
      )}
    </div>
  );
};

export default PawnShop;
